import { useEffect, useState, memo, createRef, useRef } from 'react';
import { useDispatch } from 'react-redux';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { CcfBox, CcfFastForwardIcon, CcfRewindIcon, CcfTooltip, CcfAudioPauseIcon, CcfAudioPlayIcon } from '@nice-ccf/ui-controls';
import { Box, CircularProgress, Fab, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CcfAudioPlayerStyle from './ccf-audio-player.style';
import { ATTACHMENT_ICON_NAME } from '../ccf-icon/ccf-icon-list';
import CcfIcon, { ATTACHMENT_ICON_SIZE } from '../ccf-icon/ccf-icon';
import { eventBus } from './ccf-audio-player-util';
import { AudioPlayerStatus } from './ccf-audio-player-status';
export interface CcfAudioPlayerProps {
  /**
  * @remarks  Url for playing audio in audio player
  */
  audioUrl: string;
  /**
   * @remarks  Is Inbound or Outbound
   */
  isInbound?: boolean;
  /**
   * @remarks  Method to remove attachment
   */
  handleRemoveAttachment?: (id: string) => void;
  /**
   * @remarks  Attachment object 
   */
  attachment?: any;
  /**
   * @remarks  Attachment Id
   */
  attachmentId?: string;
  /**
   * @remarks  Method to download attachment
   */
  handleDownloadAttachment?: (attachment: any) => void;
  /**
   * @remarks  Should show close icon
   */
  shouldShowCloseIcon?: boolean;
  /**
   * @remarks  Should show download icon
   */
  shouldShowDownloadIcon?: boolean;
  /**
   * @remarks  Audio player width
   */
  audioPlayerWidth?: string;
  /**
   * @remarks check if recording in progress
   */
  isVoiceRecordingInprogress?: boolean;
  /**
   * @remarks  File name
   */
  fileName?: string;
}

/**
 * Component to render audio player
 * @returns audio player
 * @example - CcfAudioPlayer 
 */
export function CcfAudioPlayer(props: CcfAudioPlayerProps) {
  const { audioUrl, handleRemoveAttachment, attachmentId, attachment,
    handleDownloadAttachment, audioPlayerWidth, isInbound, fileName,
    shouldShowCloseIcon, shouldShowDownloadIcon, isVoiceRecordingInprogress } = props;
  const player = createRef<AudioPlayer>();
  const [audio, setAudio] = useState<string>('');
  let timerInterval: ReturnType<typeof setTimeout>;
  const secondsRef = useRef(0);
  let seconds = secondsRef.current;
  const [recordingTimer, setRecordingTimer] = useState('00:00');
  // Dev note- Second is now hard coded but we have story for handling circular progress below constant will be changed
  const second = 60;
  const theme = useTheme();
  const audioRecordingtheme = CcfAudioPlayerStyle(theme, audioPlayerWidth, isInbound, shouldShowCloseIcon ?? false);
  const dispatch = useDispatch();

  /**
      * Method to Update Timer for voice recording
      * @example - updateTimer
      */
  const updateTimer = () => {
    seconds++;
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    setRecordingTimer(`${minutes}:${secs}`);
  }

  useEffect(() => {
    if (isVoiceRecordingInprogress) {
      timerInterval = setInterval(updateTimer, 1000);
    } else {
      clearInterval(timerInterval);
      secondsRef.current = 0;
      setRecordingTimer('00:00');
    }

  }, [isVoiceRecordingInprogress]);

  const customIcons = {
    rewind: !isVoiceRecordingInprogress && <CcfRewindIcon color={'primary'} style={{ margin:'10 0 10 0',display: 'block', width: '-webkit-fill-available', height: 'revert-layer', overflow: 'visible' }} />,
    forward: !isVoiceRecordingInprogress && < CcfFastForwardIcon viewBox="0 0 16 20" color={'primary'} style={{ margin: '10 0 10 0',display: 'block', width: '-webkit-fill-available', height: 'revert-layer',overflow:'visible' }} />,
    pause: <Fab
      color="primary"
      sx={audioRecordingtheme.fab}
    ><CcfAudioPauseIcon viewBox="0 0 20 20" sx={audioRecordingtheme.audioHoldIcon} />
    </Fab>,
    play: isVoiceRecordingInprogress ?
      <Box sx={audioRecordingtheme.pulseAnimation}>
        <Fab sx={{ ...audioRecordingtheme.fabButton, bottom: '0.625rem', right: '0.313rem' }} onClick={() => eventBus.emit(AudioPlayerStatus.STOP_RECORDING, 'Stop Recording')}><CcfIcon
          attachmentIcon={ATTACHMENT_ICON_NAME.RECORDING_IN_PROGRESS}
          customStyle={audioRecordingtheme.recordInprogress}
          size={ATTACHMENT_ICON_SIZE.SMALL} /></Fab>
      </Box>
      :<Fab
        color="primary"
        sx={audioRecordingtheme.fab}
      > 
        <CcfAudioPlayIcon viewBox="0 0 20 20" sx={audioRecordingtheme.audioResumeIcon} /> 
      </Fab>,
  };
  /**
 * Method to handle downlaod attachment
 * @example - downloadAttachment()
 */
  // const downloadAttachment = () =>{
  //   attachmentDownload && setAttachmentDownload(true);
  // }

  useEffect(() => {
    if (audioUrl) {
      const newAudio = new Audio(audioUrl);
      if (audioUrl.includes('downloadAttachmentToken')){
        setAudio(newAudio.src +'&partial=true');
      }else{
        setAudio(newAudio.src);
      }
    }
  }, [audioUrl]);

  return (
    <Box>
      {
        <Box sx={audioRecordingtheme.audioPlayerPlayButton}>
          <Box sx={audioRecordingtheme.mainContainer}>
            <CcfTooltip title={fileName ?? ''} disableHoverListener={!fileName}>
              <Box sx={audioRecordingtheme.tooltipArea}>
                <AudioPlayer
                  ref={player}
                  style={audioRecordingtheme.audioAttachmentContainer}
                  customIcons={customIcons}
                  showJumpControls={isVoiceRecordingInprogress ? false : true}
                  layout="horizontal-reverse"
                  preload='auto'
                  src={audio}
                  autoPlayAfterSrcChange={false} // Set to true if you want the audio to play automatically
                  autoPlay={false} // Set to true if you want the audio to play automatically
                  progressJumpSteps={{ backward: 10000, forward: 10000 }}  
                  customAdditionalControls={[]}
                  customProgressBarSection={[
                    RHAP_UI.PROGRESS_BAR,
                    !isVoiceRecordingInprogress ? RHAP_UI.CURRENT_LEFT_TIME : <CcfBox sx={audioRecordingtheme.recordTimer}>{recordingTimer}</CcfBox>,
                    <>
                      {shouldShowCloseIcon &&
                        <CcfBox>
                          <CloseIcon
                            data-testid='Close'
                            sx={audioRecordingtheme.closeIcon}
                            onClick={() => {
                              // dispatch(CcfAssignmentAction.removeAudioRecording(true));
                              handleRemoveAttachment && handleRemoveAttachment(attachmentId ?? '')
                            }}
                            fontSize="small"
                          />
                        </CcfBox>}
                      {shouldShowDownloadIcon &&
                        <CcfBox
                        sx={{margin: '0 10px 0 10px'}}
                          data-testid='Download'
                          onClick={() => {
                            handleDownloadAttachment && handleDownloadAttachment(attachment ?? {} as any)
                          }}>
                          <CcfIcon
                            attachmentIcon={ATTACHMENT_ICON_NAME.DOWNLOAD}
                            customStyle={audioRecordingtheme.downloadIcon}
                            size={ATTACHMENT_ICON_SIZE.SMALL} />
                        </CcfBox>
                      }
                    </>
                  ]}
                  customVolumeControls={[]}
                />
              </Box>
            </CcfTooltip>
          </Box>
        </Box>
      }
    </Box>

  );
}

export default memo(CcfAudioPlayer);
